import { useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Button from "../../../../../../components/atoms/button";
import CheckBox from "../../../../../../components/atoms/checkboxes";

import { useAppDispatch, useAppSelector } from "../../../../../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import {
  useStartAssessmentMutation,
  useUpdateSessionIdMutation
} from "../../../../../../store/slices/api/assessmentApiSlice";

import { ProctoringFunctionsContext } from "../../../../../../contexts/proctoringFunctionsContext";

import { requestFullScreen } from "../../../../../../utils/global/fullScreenFunctions";
import { useShowErrors } from "../../../toasterWrapper";
import { removeInlineColors } from "@assessment/utils/global/removeInlineColor";
import orgBasedFeatureNameMapping from "@assessment/utils/orgBasedFeatureName";
import { generateUniqueId } from "../generateId";

interface Props {
  statement: string;
  setActiveStep: (val: "next" | "previous") => void;
}

const TermsAndConditions = ({ statement, setActiveStep }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const showErrors = useShowErrors();

  const { scheduleId } = useParams();

  const [startAssessment] = useStartAssessmentMutation();
  const [updateTabId] = useUpdateSessionIdMutation();

  const context = useContext(ProctoringFunctionsContext);
  if (!context) {
    throw new Error("Context Error");
  }

  const { startProctoring } = context;

  const [checked, setChecked] = useState(false);
  const [submittedForm, setSubmittedForm] = useState(false);

  const videoProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.videoProctoring
  );
  const user = useAppSelector((state) => state.UserInfoSlice.profile);

  const browserProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.browserProctoring
  );

  const orgBasedFeatureName =
    orgBasedFeatureNameMapping[(user?.org as string) || "default"] ||
    orgBasedFeatureNameMapping["default"];

  const updatedStatement = removeInlineColors(statement);

  const startAssessmentHandler = async () => {
    setSubmittedForm(true);
    if (!checked) {
      setSubmittedForm(false);
      return;
    }

    try {
      if (!scheduleId) {
        throw {
          status: "",
          title: "Schedule Id Not Found",
          err: {},
          description: ""
        };
      }

      const tncAcceptedAt = new Date().toISOString();

      const res = await startAssessment({
        scheduleId: scheduleId,
        tncAccepted: true,
        tncAcceptedAt: tncAcceptedAt
      });

      if (res.error) {
        throw {
          status: "",
          title: `failed to start the ${orgBasedFeatureName}`,
          err: res.error,
          description: ""
        };
      }

      const ongoingId = res?.data?.data?.id;

      if (ongoingId) {
        dispatch(AssessmentSliceActions.mutateOnGoingId(ongoingId));
        const tabId = await generateUniqueId();
        if (tabId) {
          const body = {
            ongoingId: ongoingId,
            scheduleId: scheduleId,
            sessionId: tabId
          };
          const res1 = await updateTabId(body);
          dispatch(AssessmentSliceActions.mutateVisitorId(tabId));
        }
      }

      const proctoringStarted = await startProctoring();
      if (videoProctoring && !proctoringStarted) {
        throw {
          status: "",
          title: "Unable to start secure proctoring",
          err: {},
          description: "Please retry camera/proctoring setup and try again."
        };
      }

      dispatch(AssessmentSliceActions.mutateShowWarningModal(false));
      dispatch(AssessmentSliceActions.mutateShowProctoringVideoPreview(true));
      dispatch(AssessmentSliceActions.mutateAssessmentHasStarted(true));
      if (videoProctoring || browserProctoring) requestFullScreen(dispatch);
      setSubmittedForm(false);
    } catch (err: unknown) {
      console.error(err);
      const temp = err as {
        title?: string;
        description?: string;
        err?: { data?: { error?: string[] } };
      };
      const rawErrList = temp?.err?.data?.error;
      const errorTitle = temp?.title ?? "Something went wrong";
      const errList: string[] = (rawErrList ?? []).map(
        (el: string) => el.split("_")[0]
      );
      if (showErrors)
        showErrors(errorTitle, errList, "error", temp?.description);
      setSubmittedForm(false);
    }
  };

  return (
    <>
      <div className="flex flex-col h-[70vh] overflow-y-auto p-8 border border-border rounded-xl thin-scrollbar">
        <h2 className="text-xl font-semibold">
          {t("assessment.stepper.termsAndConditions")}
        </h2>

        {/* Wrapping p inside a flex-grow container */}
        <div className="flex-1 mt-5">
          <p
            dangerouslySetInnerHTML={{ __html: updatedStatement }}
            className="h-full overflow-y-auto text-base font-normal text-secondary"
          />
        </div>

        <div className="mt-5">
          <CheckBox
            checked={checked}
            onChange={(value: boolean) => {
              setChecked(value);
            }}
            label={t("assessment.stepper.agreeTerms")}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-5 mt-10 md:justify-end">
        <Button
          variant="outlined"
          buttonColor="secondary"
          onClick={() => {
            setActiveStep("previous");
          }}
          disabled={submittedForm}
        >
          {t("assessment.stepper.previousStep")}
        </Button>
        <Button
          variant="contained"
          onClick={startAssessmentHandler}
          disabled={!checked || submittedForm}
        >
          {t("assessment.stepper.startAssessment")}
        </Button>
      </div>
    </>
  );
};

export default TermsAndConditions;
